import { IconListDetails, IconSun } from '@tabler/icons-react';
import { useState } from 'react';
import { usePath } from 'crossroad';
import SidebarUser from '@/components/sidebar-user';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from '@/components/ui/sidebar';
import logo from '../logo.png';
import { appTitle } from '@/lib/constants';
import { SidebarSync } from './sidebar-sync';
import { BookOpen, Layers, Moon, Shield, Map, BookOpenCheck, Clock, ChevronDown, Settings, MessageSquare } from 'lucide-react';
import { toggleDarkMode } from '@/lib/utils/utils';
import { useAdminAuth } from '@/lib/hooks/use-admin-auth';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useFeedback } from '@/lib/context/feedback-context';

export default function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { setOpen, setOpenMobile } = useSidebar();
    const { openFeedback } = useFeedback();
    const darkMode = useDarkMode();
    const { isAdmin, isLoading } = useAdminAuth();
    const [path] = usePath();
    const [isContextOpen, setIsContextOpen] = useState(false);

    function closeSidebar() {
        setOpen(false);
        setOpenMobile(false);
    }

    return (
        <Sidebar collapsible="offcanvas" className="z-[150]" {...props}>
            {!isLoading && <>
                <SidebarHeader>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
                                <a href="/home">
                                    <img src={logo} width={20} height={20} alt="Logo" />
                                    <span className="text-base font-semibold">{appTitle}</span>
                                </a>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarHeader>

                <SidebarContent>
                    <SidebarGroup>
                        <SidebarGroupContent className="flex flex-col gap-2">
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild tooltip="Read the Bible" isActive={path.startsWith('/app/read') || path.startsWith('/app/passage')}>
                                        <a href="/app/read" onClick={closeSidebar}>
                                            <BookOpen />
                                            <span>Read the Bible</span>
                                        </a>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton onClick={() => setIsContextOpen(!isContextOpen)} tooltip="Context" isActive={isContextOpen}>
                                        <Layers />
                                        <span>Experimental</span>
                                        <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${isContextOpen ? 'rotate-180' : ''}`} />
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {isContextOpen && (
                                    <SidebarMenuSub>
                                        <SidebarMenuSubItem>
                                            <SidebarMenuSubButton asChild isActive={path.startsWith('/app/outline')}>
                                                <a href="/app/outline" onClick={closeSidebar}>
                                                    <IconListDetails />
                                                    <span>Biblical Outlines</span>
                                                </a>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                        <SidebarMenuSubItem>
                                            <SidebarMenuSubButton asChild isActive={path.startsWith('/app/map')}>
                                                <a href="/app/map" onClick={closeSidebar}>
                                                    <Map />
                                                    <span>Ancient World Map</span>
                                                </a>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                        <SidebarMenuSubItem>
                                            <SidebarMenuSubButton asChild isActive={path.startsWith('/app/timeline')}>
                                                <a href="/app/timeline" onClick={closeSidebar}>
                                                    <Clock />
                                                    <span>Ancient World Timeline</span>
                                                </a>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                )}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>

                <SidebarFooter>
                    <SidebarMenu>
                        {isAdmin && (
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild tooltip="Admin" isActive={path.startsWith('/app/manage')}>
                                    <a href="/app/manage" onClick={closeSidebar}>
                                        <Shield />
                                        <span>Admin</span>
                                    </a>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        )}
                        {isAdmin && (
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild tooltip="Field Guide" isActive={path.startsWith('/app/field-guide')}>
                                    <a href="/app/field-guide" onClick={closeSidebar}>
                                        <BookOpenCheck />
                                        <span>Field Guide</span>
                                    </a>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        )}
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild tooltip="Settings" isActive={path.startsWith('/app/onboarding')}>
                                <a href="/app/onboarding" onClick={closeSidebar}>
                                    <Settings />
                                    <span>Settings</span>
                                </a>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild tooltip="Dark Mode" className="cursor-pointer">
                                <a onClick={() => {
                                    toggleDarkMode(!darkMode);
                                }}>
                                    {darkMode ? <IconSun /> : <Moon />}<span>Switch to {darkMode ? 'Light' : 'Dark'} Mode</span>
                                </a>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                            <SidebarMenuButton
                                onClick={() => {
                                    openFeedback();
                                    closeSidebar();
                                }}
                                tooltip="Send Feedback"
                                className="cursor-pointer"
                            >
                                <MessageSquare />
                                <span>Send Feedback</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>

                    <SidebarSync />
                    <SidebarUser />
                </SidebarFooter>
            </>}
        </Sidebar>
    );
}